import {
  ReactElement,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import cc from 'classcat';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { redirectToUrl } from 'utils/generic/redirect-to-url';
import { AToZData, TAtoZMenuConfigs } from 'interfaces/generic';
import { TabContents } from './TabContents';
import { GlobalIcon } from 'components/GlobalIcon';
import { SwiperSlider } from 'newcomponents/UI/SwiperSlider';
import './index.less';

type Props = {
  setVisible: (arg: boolean) => void;
  setOpen: (arg: boolean) => void;
  setPage: (arg: number) => void;
  configs: TAtoZMenuConfigs;
  showActivePage?: boolean;
  data: AToZData[];
  open: boolean;
  page: number;
};

export const DataPages = ({
  data,
  open,
  setOpen,
  configs,
  setVisible,
  showActivePage
}: Props): ReactElement => {
  const lang = SpringConfigs.CURRENT_LANGUAGE;
  const [currentPage, setCurrentPage] = useState<
    AToZData | undefined | Record<string, string | number>
  >();

  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<AToZData | null>(null);
  const [azData, setAzData] = useState<AToZData[]>([]);
  const location = useLocation();
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    setAzData(data.filter(el => el.showOnAtoZ));
  }, [data]);

  useEffect(() => {
    const currentRoute = location.pathname;

    const findTab = data.find((el: AToZData) => {
      if (el.onlyBottomNav || !el.showOnAtoZ) {
        return;
      }

      if (typeof el.path !== 'string' && el.path) {
        if (el.path[lang]) {
          return `${currentRoute}/`.includes(`/${el.path[lang]}/`);
        } else {
          return `${currentRoute}/`.includes(`/${el.path.fallback}/`);
        }
      } else {
        return `${currentRoute}/`.includes(`/${el.path}/`);
      }
    });

    if (findTab) {
      setCurrentPage?.(findTab);
    }
  }, [location]);

  const drawableTabs = useMemo(() => {
    return data.map((tab, index) => {
      if (tab.onlyBottomNav || !tab.showOnAtoZ) {
        return;
      }

      const title =
        typeof tab.title !== 'string'
          ? tab.title[lang]
            ? tab.title[lang]
            : tab.title.fallback
          : tab.title;

      let path =
        typeof tab.path !== 'string'
          ? tab.path[lang]
            ? tab.path[lang]
            : tab.path.fallback
          : tab.path;

      if (path?.[0] === '/') {
        path = path.replace('/', '');
      }

      let iconPath;
      let iconUrl;

      if (tab.iconUrl) {
        iconUrl =
          tab.iconUrl.includes('https://') || tab.iconUrl.includes('http://')
            ? tab.iconUrl
            : `${SpringConfigs.USER_FILES_URL}/media/${tab.iconUrl}?v=${SpringConfigs.VERSION}`;
      } else if (tab.icon) {
        iconPath = tab.icon.split('.');
      } else {
        switch (tab.category) {
          case 'sport':
            iconPath =
              tab.type === 'live'
                ? ['generic', 'colored', 'live']
                : ['generic', 'colored', 'upcoming'];
            break;
          case 'casino':
            iconPath = ['casino', 'colored', 'placeholder'];
            break;
          default:
            iconPath = ['casino', 'colored', 'othergames'];
        }
      }

      const onClick = (e: SyntheticEvent, noRedirect: boolean): void => {
        if (noRedirect) {
          e.stopPropagation();
          setActiveTab(tab);
        } else {
          setVisible(false);
          redirectToUrl(path, tab);
        }
      };

      return (
        <div
          key={index}
          className={cc([
            'menu-item',
            { 'menu-item-active': activeTab?.id === tab.id }
          ])}
          onClick={e =>
            onClick(e, Boolean(tab.category && tab.type !== 'live-casino'))
          }
        >
          <GlobalIcon
            lib={iconPath?.[0]}
            theme={iconPath?.[1]}
            name={iconPath?.[2]}
            size={EIconSize._24}
            url={iconUrl}
          />
          <p
            className={cc([
              'tabTitle',
              { tabTitle__active: currentPage?.id === tab.id }
            ])}
          >
            {title}
          </p>
        </div>
      );
    });
  }, [data, activeTab, currentPage]);

  const findActiveKey = () => {
    let index = 0;

    const setFirstNotRedirectableTab = () => {
      const firstNotRedirectableTabIndex = azData.findIndex(
        tab => !!tab?.category
      );

      setActiveSlide(firstNotRedirectableTabIndex);
      setActiveTab(azData[firstNotRedirectableTabIndex]);
    };

    if (currentPage && showActivePage) {
      index = azData.findIndex(tab => currentPage?.id === tab?.id);

      if (azData[index].category) {
        setActiveTab(azData[index]);
        setActiveSlide(index);
      } else {
        setFirstNotRedirectableTab();
      }
    } else {
      setFirstNotRedirectableTab();
    }
  };

  useEffect(() => {
    findActiveKey();
  }, [currentPage, open, showActivePage, azData]);

  return (
    <div className="az-tab-wrapper">
      <SwiperSlider
        drawableTabs={drawableTabs.filter(el => el)}
        activeSlide={activeSlide}
        headerArrows={configs.sliderArrowsAToZ}
        headerTitle={t('generic.seeMore')}
      />
      <TabContents
        activeTab={activeTab}
        withArrows={configs.sliderArrowsAToZ}
        setOpen={setOpen}
        configs={configs}
        setVisible={setVisible}
      />
    </div>
  );
};
